import { Typography, Paper } from '@gapartners/ui';
import { useTranslation } from 'react-i18next';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { SentimentBreakdownDTO } from '@/types/qualityMetrics';

interface SentimentChartProps {
  data: SentimentBreakdownDTO;
  title?: string;
  height?: number;
}

const SENTIMENT_COLORS = {
  positive: '#4CAF50',
  neutral: '#FFC107',
  negative: '#F44336',
  grey: '#CCCCCC',
};

export function SentimentChart({
  data,
  title = 'Sentiment Distribution',
  height = 300,
}: SentimentChartProps) {
  const { t } = useTranslation();

  // Calculate total sentiment
  const totalSentiment = data.positive + data.neutral + data.negative;
  const hasNoSentiment = totalSentiment === 0;

  // Build chart data with actual sentiments and grey background/remainder
  let chartData: Array<{ name: string; value: number; color: string }> = [];

  if (hasNoSentiment) {
    // Show 100% grey when no sentiment data
    chartData = [
      {
        name:
          t('pages.manager.qualityMetrics.call.components.grey') || 'No data',
        value: 100,
        color: SENTIMENT_COLORS.grey,
      },
    ];
  } else {
    // Add actual sentiments (even if 0, to show in legend)
    if (data.positive > 0) {
      chartData.push({
        name: t('pages.manager.qualityMetrics.call.components.positive'),
        value: data.positive,
        color: SENTIMENT_COLORS.positive,
      });
    }
    if (data.neutral > 0) {
      chartData.push({
        name: t('pages.manager.qualityMetrics.call.components.neutral'),
        value: data.neutral,
        color: SENTIMENT_COLORS.neutral,
      });
    }
    if (data.negative > 0) {
      chartData.push({
        name: t('pages.manager.qualityMetrics.call.components.negative'),
        value: data.negative,
        color: SENTIMENT_COLORS.negative,
      });
    }

    // Add grey slice for remaining percentage
    const greyValue = 100 - totalSentiment;
    if (greyValue > 0) {
      chartData.push({
        name:
          t('pages.manager.qualityMetrics.call.components.grey') || 'No data',
        value: greyValue,
        color: SENTIMENT_COLORS.grey,
      });
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, height }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={height - 100}>
        <PieChart margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
          <Pie
            data={chartData}
            cx="50%"
            cy="45%"
            labelLine={false}
            outerRadius={70}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
          <Legend verticalAlign="bottom" height={36} />
        </PieChart>
      </ResponsiveContainer>
    </Paper>
  );
}
